/**
 * f14.2: the reconciliation queue -- `/cycles/:cycleId/reconciliation`
 * (design D8, laid out after `cycle-step.html` / `cycle-workflow.html`).
 *
 * The filters live in the URL so f14.3 can link here narrowed to one batch
 * (`?batch_id=`). The default view is Pending. Every decision goes through
 * the "This decision is final" dialog; a `409` shows the server's message as
 * a toast and the list refetches (the hook invalidates). Finance/Controller
 * and Platform Admin only -- the server answers 403 to anyone else, and this
 * page shows that answer rather than deciding it.
 */

import { useState } from 'react';
import { Inbox, Lock, TriangleAlert, X } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import type { ReconciliationQuery, ReconciliationVariance } from '@/api/client';
import { RecordUnavailable } from '@/ui/RecordUnavailable';
import '@/styles/review.css';
import '@/styles/cycle-export.css';
import '@/styles/reconciliation.css';

import { DecisionConfirmDialog } from './DecisionConfirmDialog';
import { ReconciliationSummaryCard } from './ReconciliationSummaryCard';
import { VarianceTable } from './VarianceTable';
import { SOURCE_LABELS, STATUS_LABELS, isReadOnlyCycle, monthLabel } from './reconciliationFormat';
import { decisionFailure, useDecideVariance, useReconciliationSummary, useVarianceList } from './useReconciliation';

const PAGE_SIZE = 50;
const ALL = 'ALL';

function readQuery(params: URLSearchParams): ReconciliationQuery {
  const status = params.get('status') ?? 'PENDING';
  const month = Number(params.get('period_month'));
  return {
    status: status === ALL ? undefined : status,
    source: params.get('source') || undefined,
    business_unit_id: params.get('business_unit_id') || undefined,
    client_id: params.get('client_id') || undefined,
    period_month: month >= 1 && month <= 12 ? month : undefined,
    batch_id: params.get('batch_id') || undefined,
    page: Math.max(1, Number(params.get('page')) || 1),
    page_size: PAGE_SIZE,
  };
}

interface Pending {
  variance: ReconciliationVariance;
  decision: 'ACCEPT' | 'REJECT';
}

export function ReconciliationQueuePage() {
  const { cycleId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const query = readQuery(params);
  const list = useVarianceList(cycleId, query);
  const summary = useReconciliationSummary(cycleId);
  const decide = useDecideVariance(cycleId);
  const [pending, setPending] = useState<Pending | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };

  const confirm = (comment: string | null) => {
    if (!pending) return;
    decide.mutate(
      { varianceId: pending.variance.id, decision: pending.decision, comment },
      {
        onSettled: () => setPending(null),
        onError: (error) => setToast(decisionFailure(error)),
      },
    );
  };

  const failure = list.error ?? summary.error;
  if (failure) {
    return (
      <RecordUnavailable
        error={failure}
        backTo={`/cycles/${encodeURIComponent(cycleId)}`}
        backLabel="Back to the cycle"
        onRetry={() => void (list.isError ? list.refetch() : summary.refetch())}
      />
    );
  }

  const data = list.data;
  const stateCode = data?.cycle_state.code ?? summary.data?.cycle_state.code ?? '';
  const readOnly = isReadOnlyCycle(stateCode);
  const statusValue = params.get('status') ?? 'PENDING';
  const units = summary.data?.pending_by_unit ?? [];
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const page = query.page ?? 1;

  return (
    <>
      <div className="page-head">
        <div>
          <nav className="header-crumb mb-4" aria-label="Breadcrumb">
            <Link to="/cycles">Cycles</Link> <span>/</span>{' '}
            <Link to={`/cycles/${encodeURIComponent(cycleId)}`}>Cycle</Link> <span>/</span> <b>Reconciliation</b>
          </nav>
          <h1 className="page-title">Reconciliation queue</h1>
          <div className="page-sub">
            Closed months compared with the frozen baseline and the actuals · every decision is final and attributed
          </div>
        </div>
      </div>

      {readOnly && (
        <div className="alert alert-neutral mb-4" role="status">
          <Lock aria-hidden="true" />
          <div>
            <div className="a-title">The cycle is {stateCode === 'SUBMITTED' ? 'submitted' : 'frozen'}</div>
            <div className="a-body">Variances are read-only. Reopen the cycle to decide the pending ones.</div>
          </div>
        </div>
      )}

      <ReconciliationSummaryCard list={data} summary={summary.data} />

      <div className="card">
        <div className="card-head">
          <h3>Variances</h3>
          <span className="hint">{data ? `${data.total} shown · Pending first` : 'Loading…'}</span>
        </div>

        <div className="toolbar rc-toolbar">
          <select aria-label="Status" className="filter-select" value={statusValue}
            onChange={(event) => setParam('status', event.target.value)}>
            {Object.entries(STATUS_LABELS).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            <option value={ALL}>All statuses</option>
          </select>
          <select aria-label="Source" className="filter-select" value={query.source ?? ''}
            onChange={(event) => setParam('source', event.target.value || null)}>
            <option value="">All sources</option>
            {Object.entries(SOURCE_LABELS).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select>
          <select aria-label="Business Unit" className="filter-select" value={query.business_unit_id ?? ''}
            onChange={(event) => setParam('business_unit_id', event.target.value || null)}>
            <option value="">All Business Units</option>
            {units.map((unit) => (
              <option key={unit.business_unit.id} value={unit.business_unit.id}>
                {unit.business_unit.name} ({unit.pending} pending)
              </option>
            ))}
          </select>
          <select aria-label="Month" className="filter-select" value={query.period_month ?? ''}
            onChange={(event) => setParam('period_month', event.target.value || null)}>
            <option value="">All months</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
              <option key={month} value={month}>{monthLabel(month)}</option>
            ))}
          </select>
          {query.client_id && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setParam('client_id', null)}>
              One client <X aria-hidden="true" />
            </button>
          )}
          {query.batch_id && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setParam('batch_id', null)}>
              One ingest batch <X aria-hidden="true" />
            </button>
          )}
        </div>

        {data && data.items.length === 0 ? (
          <div className="empty">
            <div className="e-icon"><Inbox aria-hidden="true" /></div>
            <h3>{statusValue === 'PENDING' ? 'No variances to decide' : 'No variances match these filters'}</h3>
            <p>
              {statusValue === 'PENDING'
                ? 'Every closed month agrees with its reconciled figure.'
                : 'Change a filter to see more.'}
            </p>
          </div>
        ) : (
          <VarianceTable
            items={data?.items ?? []}
            cycleStateCode={stateCode}
            loading={list.isFetching}
            onDecide={(variance, decision) => setPending({ variance, decision })}
          />
        )}

        {pages > 1 && (
          <div className="rc-pager">
            <button type="button" className="btn btn-secondary btn-sm" disabled={page <= 1}
              onClick={() => setParam('page', String(page - 1))}>Previous</button>
            <span className="hint">Page {page} of {pages}</span>
            <button type="button" className="btn btn-secondary btn-sm" disabled={page >= pages}
              onClick={() => setParam('page', String(page + 1))}>Next</button>
          </div>
        )}
      </div>

      {pending && (
        <DecisionConfirmDialog
          variance={pending.variance}
          decision={pending.decision}
          busy={decide.isPending}
          onConfirm={confirm}
          onClose={() => setPending(null)}
        />
      )}

      {toast && (
        <div className="alert alert-danger cx-toast" role="alert">
          <TriangleAlert aria-hidden="true" />
          <div className="a-title">{toast}</div>
          <button type="button" className="cx-toast-close" onClick={() => setToast(null)} aria-label="Dismiss">
            <X aria-hidden="true" />
          </button>
        </div>
      )}
    </>
  );
}
